import { FormEvent } from "react";
import { EmptyState, ErrorState, FreshnessBar, LoadingState, formatDate } from "../components/AdminPageStates";
import MetricCard from "../components/MetricCard";
import { useRevenueAdmin } from "../hooks/useRevenueAdmin";

function formatCurrency(value: number | null) {
  return value === null
    ? "Unavailable"
    : new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value);
}

function metricState(value: number | null) {
  return value === null ? "unavailable" : "ready";
}

export default function Revenue() {
  const { data, error, filters, isLoading, refresh, setFilters } = useRevenueAdmin();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void refresh(filters);
  }

  if (isLoading && !data) {
    return <LoadingState message="Loading revenue records." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!data) {
    return <EmptyState message="No revenue records were found." />;
  }

  return (
    <>
      <FreshnessBar
        dataSource="marketplace_transactions / coach_earnings / coach_earning_adjustments / coach_payouts"
        isLive={false}
        lastUpdated={data.lastUpdated}
        onRefresh={() => {
          void refresh(filters);
        }}
      />

      <form className="filter-panel revenue-filter-panel" onSubmit={handleSubmit}>
        <label>
          Date range start
          <input
            type="date"
            value={filters.startDate}
            onChange={(event) => setFilters({ ...filters, startDate: event.target.value })}
          />
        </label>
        <label>
          Date range end
          <input
            type="date"
            value={filters.endDate}
            onChange={(event) => setFilters({ ...filters, endDate: event.target.value })}
          />
        </label>
        <button type="submit" className="primary-button">
          Apply
        </button>
      </form>

      <div className="overview-grid">
        <MetricCard
          title="Total GMV"
          value={formatCurrency(data.totalGMV)}
          note={data.totalGMV === null ? "TODO: marketplace_transactions aggregate unavailable. Check admin rules and status + createdAt index." : "Paid marketplace_transactions grossAmountUSD for selected range, or all available paid transactions."}
          state={metricState(data.totalGMV)}
        />
        <MetricCard
          title="Platform Revenue"
          value={formatCurrency(data.platformRevenue)}
          note={data.platformRevenue === null ? "TODO: marketplace_transactions platformFeeUSD aggregate unavailable." : "Paid marketplace_transactions platformFeeUSD for selected range."}
          state={metricState(data.platformRevenue)}
        />
        <MetricCard
          title="Revenue This Month"
          value={formatCurrency(data.revenueThisMonth)}
          note={data.revenueThisMonth === null ? "TODO: marketplace_transactions month aggregate unavailable. Check admin rules and status + createdAt index." : "Paid marketplace_transactions grossAmountUSD for the local admin month."}
          state={metricState(data.revenueThisMonth)}
        />
        <MetricCard
          title="Paid Coach Earnings"
          value={formatCurrency(data.paidCoachEarnings)}
          note={data.paidCoachEarnings === null ? "TODO: coach_earnings paid aggregate unavailable." : "Sum of coach_earnings net_amount_usd where status is paid."}
          state={metricState(data.paidCoachEarnings)}
        />
        <MetricCard
          title="Paid Payouts"
          value={formatCurrency(data.paidPayouts)}
          note={data.paidPayouts === null ? "TODO: coach_payouts paid aggregate unavailable." : "Sum of coach_payouts amount_usd where status is paid."}
          state={metricState(data.paidPayouts)}
        />
        <MetricCard
          title="Open Refund Adjustments"
          value={formatCurrency(data.openRefundAdjustmentBalance)}
          note={data.openRefundAdjustmentBalance === null ? "TODO: coach_earning_adjustments aggregate unavailable." : "Outstanding post-payout refund balance to reconcile before future payouts."}
          state={metricState(data.openRefundAdjustmentBalance)}
        />
      </div>

      <section className="detail-section">
        <h2>Revenue Trend</h2>
        {data.trendRows.length > 0 ? (
          <div className="table-shell">
            <table className="admin-table trend-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Transactions</th>
                  <th>Gross revenue</th>
                  <th>Chor fee</th>
                  <th>Net earnings</th>
                </tr>
              </thead>
              <tbody>
                {data.trendRows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{row.transactionCount}</td>
                    <td>{formatCurrency(row.grossRevenue)}</td>
                    <td>{formatCurrency(row.platformFees)}</td>
                    <td>{formatCurrency(row.coachEarnings)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-inline">No trend rows are available for the current filters.</p>
        )}
      </section>

      <section className="detail-section">
        <h2>Transactions</h2>
        {data.transactions.length > 0 ? (
          <div className="table-shell">
            <table className="admin-table revenue-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Payer</th>
                  <th>Recipient/provider</th>
                  <th>Provider</th>
                  <th>Buyer</th>
                  <th>Payment type</th>
                  <th>Payment flow</th>
                  <th>Funds status</th>
                  <th>Release attention</th>
                  <th>Gross amount</th>
                  <th>Amount collected</th>
                  <th>Chor fee</th>
                  <th>Provider payout</th>
                  <th>Net earnings</th>
                  <th>Booking</th>
                  <th>Payment intent</th>
                  <th>Transfer</th>
                  <th>Released at</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{formatDate(transaction.date)}</td>
                    <td>{transaction.payer}</td>
                    <td>{transaction.recipient}</td>
                    <td>{transaction.provider}</td>
                    <td>{transaction.buyer}</td>
                    <td>{transaction.paymentType}</td>
                    <td>{transaction.paymentFlow ?? "Unavailable"}</td>
                    <td>{transaction.fundsStatus ?? "Unavailable"}</td>
                    <td>{transaction.releaseFailureMessage ?? (transaction.fundsStatus === "release_failed" ? "Requires attention" : "None")}</td>
                    <td>{formatCurrency(transaction.grossAmount)}</td>
                    <td>{formatCurrency(transaction.amountCollected)}</td>
                    <td>{formatCurrency(transaction.platformFee)}</td>
                    <td>{formatCurrency(transaction.providerPayout)}</td>
                    <td>{formatCurrency(transaction.netEarnings)}</td>
                    <td>{transaction.serviceBookingId ?? "Unavailable"}</td>
                    <td>{transaction.stripePaymentIntentId ?? "Unavailable"}</td>
                    <td>{transaction.stripeTransferId ?? "Not released"}</td>
                    <td>{formatDate(transaction.releasedAt)}</td>
                    <td>{transaction.status ?? "Unavailable"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-inline">No transactions match the current filters.</p>
        )}
      </section>

      <section className="detail-section">
        <h2>Refund Adjustments</h2>
        {data.refundAdjustments.length > 0 ? (
          <div className="table-shell">
            <table className="admin-table revenue-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Coach</th>
                  <th>Earning</th>
                  <th>Booking</th>
                  <th>Transaction</th>
                  <th>Refund</th>
                  <th>Adjustment</th>
                  <th>Remaining</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.refundAdjustments.map((adjustment) => (
                  <tr key={adjustment.id}>
                    <td>{formatDate(adjustment.createdAt)}</td>
                    <td>{adjustment.coachUserId ?? "Unavailable"}</td>
                    <td>{adjustment.earningId ?? "Unavailable"}</td>
                    <td>{adjustment.bookingId ?? "Unavailable"}</td>
                    <td>{adjustment.marketplaceTransactionId ?? "Unavailable"}</td>
                    <td>{adjustment.refundId ?? "Unavailable"}</td>
                    <td>{formatCurrency(adjustment.adjustmentAmountUSD)}</td>
                    <td>{formatCurrency(adjustment.remainingAmountUSD)}</td>
                    <td>{adjustment.status ?? "Unavailable"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-inline">No refund adjustments are currently visible.</p>
        )}
      </section>

      {data.unavailable.length > 0 ? (
        <section className="detail-section">
          <h2>Unavailable Stripe / Payment Data</h2>
          {data.unavailable.map((item) => (
            <p className="detail-text" key={item}>
              {item}
            </p>
          ))}
        </section>
      ) : null}
    </>
  );
}
